import { twMerge } from "tailwind-merge";

import programArtPattern from "@/assets/images/user/programArtPattern.png";
import programArtPatternM from "@/assets/images/user/programArtPatternM.png";
import programBanStar from "@/assets/images/user/programBanStar.svg";
import programFestaPattern from "@/assets/images/user/programFestaPattern.png";
import programFestaPatternM from "@/assets/images/user/programFestaPatternM.png";
import programFocusPattern from "@/assets/images/user/programFocusPattern.png";
import programFocusPatternM from "@/assets/images/user/programFocusPatternM.png";
import programPlayPattern from "@/assets/images/user/programPlayPattern.png";
import programPlayPatternM from "@/assets/images/user/programPlayPatternM.png";
import programStagePattern from "@/assets/images/user/programStagePattern.png";
import programStagePatternM from "@/assets/images/user/programStagePatternM.png";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/**
 * 아코디언 배너 디자인은 연도별로 완전히 다르다.
 * - "legacy" : 2023 / 2024 / 2025 아카이브. 연도별 배경 이미지(item.background) + 흰색 3px 프레임.
 * - "2026"   : 2026 프로그램 페이지. 그라디언트 배경 + 전용 패턴 + 별 데코 + 흰색 4px 프레임.
 * 새 연도 디자인이 나오면 여기에 값을 추가하고, 이전 연도는 그대로 두면 아카이브 이관 시에도 디자인이 유지된다.
 */
export type ProgramAccordionDesign = "legacy" | "2026";

interface ProgramAccordionItem {
    title: string | React.ReactNode;
    background: string;
    img: string;
    contents?: React.ReactNode;
    pattern?: string;
    patternStyle?: React.CSSProperties;
    /** 배너 좌측 이미지 크기 보정 (아이콘 대신 로고 락업을 쓰는 About 페이지 등) */
    imgClassName?: string;
}

interface ProgramAccordionProps {
    list: ProgramAccordionItem[];
    className?: string;
    triggerClassName?: string;
    contentClassName?: string;
    defaultOpenItems?: number[];
    pattern?: string;
    deco?: string;
    disableToggle?: boolean;
    hideItemBorder?: boolean;
    /** 배너 디자인 연도. 미지정 시 아카이브(2023~2025) 디자인 */
    design?: ProgramAccordionDesign;
}

// 2026 디자인 토큰 (배경 그라디언트 + 테두리색 + 패턴)
// key = 각 프로그램 페이지가 넘기는 background 클래스명(2025 시절 이름을 그대로 재사용 중).
const PROGRAM_DESIGN_2026: Record<string, { gradient: string; border: string; pattern: string; patternM: string }> = {
    "bg-program202501": {
        gradient: "from-[#FF5B2E] to-[#FF7F71]",
        border: "border-[#FF5B2E]",
        pattern: programStagePattern,
        patternM: programStagePatternM,
    },
    "bg-program202502": {
        gradient: "from-[#00B1AA] to-[#44DDD7]",
        border: "border-[#00A8A0]",
        pattern: programPlayPattern,
        patternM: programPlayPatternM,
    },
    "bg-program202503": {
        gradient: "from-[#D02F8B] to-[#F46AB9]",
        border: "border-[#EB008B]",
        pattern: programArtPattern,
        patternM: programArtPatternM,
    },
    "bg-program202504": {
        gradient: "from-[#FABF00] to-[#FFDA64]",
        border: "border-[#F6921E]",
        pattern: programFocusPattern,
        patternM: programFocusPatternM,
    },
    "bg-program202505": {
        gradient: "from-[#006CB8] to-[#349CE5]",
        border: "border-[#006AC1]",
        pattern: programFestaPattern,
        patternM: programFestaPatternM,
    },
};

const TRIGGER_BASE_CLASS =
    "relative overflow-hidden p-[4px] hover:no-underline [&>svg]:mr-[8px] [&>svg]:stroke-1 [&>svg]:text-white md:[&>svg]:mr-[20px] [&_svg]:size-[24px] md:[&_svg]:size-[60px]";

interface TriggerProps {
    item: ProgramAccordionItem;
    triggerClassName?: string;
    isStatic: boolean;
}

/** 2026 디자인 배너 */
function Trigger2026({
    item,
    triggerClassName,
    isStatic,
    theme,
}: TriggerProps & { theme: (typeof PROGRAM_DESIGN_2026)[string] }) {
    return (
        <AccordionTrigger
            className={twMerge(
                TRIGGER_BASE_CLASS,
                isStatic && "cursor-default [&>svg]:hidden",
                `bg-gradient-to-r ${theme.gradient} border border-solid ${theme.border}`,
            )}
        >
            {/* 패턴: 통짜 이미지 center 고정 · 세로 반복 없음 · 모바일은 전용(작은 모티프) 이미지 */}
            <img
                src={theme.patternM}
                alt=""
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 h-[120px] w-auto max-w-none -translate-x-1/2 -translate-y-[59%] md:hidden"
            />
            <img
                src={theme.pattern}
                alt=""
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[140px] w-auto max-w-none -translate-x-1/2 -translate-y-[59%] md:block"
            />
            {/* 우측 별/반짝임 */}
            <img
                src={programBanStar}
                alt=""
                aria-hidden
                className="pointer-events-none absolute right-0 top-1/2 w-[200px] max-w-none -translate-y-1/2 mix-blend-screen md:w-[382px]"
            />
            {/* 흰색 4px 테두리 프레임 (컬러 테두리 바로 안쪽, 간격 없음) */}
            <div className="pointer-events-none absolute inset-0 z-[1] border-4 border-solid border-white" />
            <div
                className={twMerge(
                    // 트리거 전체 높이 목표: PC·태블릿 80px / 모바일(1줄) 64px.
                    // 고정 height 대신 py로 맞춤 = 외곽 10px(p-[4px]×2 + border 1px×2) + py×2 + 텍스트 line-box(font×1.4).
                    "relative z-[1] w-full flex items-center justify-between px-[8px] py-[14.4px] md:py-[12.6px] xl:px-[20px]",
                    triggerClassName,
                )}
            >
                <img src={item.img} alt="프로그램" className={twMerge("h-[16px] md:h-[auto]", item.imgClassName)} />
                {/* 각 페이지가 title 로 <p> 를 넘기므로 래퍼는 span (p 안에 p 중첩 방지) */}
                <span className="text-left font-['GongGothicMedium'] text-[16px] text-white md:text-[24px]">
                    {item.title}
                </span>
            </div>
        </AccordionTrigger>
    );
}

/** 2023 ~ 2025 아카이브 디자인 배너 */
function TriggerLegacy({
    item,
    triggerClassName,
    isStatic,
    pattern,
    deco,
}: TriggerProps & { pattern?: string; deco?: string }) {
    const itemPattern = item.pattern || pattern;

    return (
        <AccordionTrigger
            className={`${TRIGGER_BASE_CLASS}${isStatic ? " cursor-default" : ""} ${isStatic ? " [&>svg]:hidden" : ""} ${item.background} bg-cover`}
        >
            {itemPattern && (
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage: `url(${itemPattern})`,
                        backgroundPosition: "left top",
                        backgroundRepeat: "repeat",
                        opacity: 0.1,
                        ...item.patternStyle,
                    }}
                />
            )}
            {deco && itemPattern && (
                <div
                    className="pointer-events-none absolute inset-0 mix-blend-screen"
                    style={{
                        backgroundImage: `url(${deco})`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "calc(100% + 1px) -127px",
                        backgroundSize: "382px 258px",
                    }}
                />
            )}
            <div className="absolute left-[4px] top-[4px] h-[calc(100%-8px)] w-[calc(100%-8px)] border-[3px] border-white" />
            <div
                className={twMerge(
                    "w-full flex items-center justify-between px-[8px] py-[15px] xl:px-[20px]",
                    triggerClassName,
                )}
            >
                <img src={item.img} alt="프로그램" className={twMerge("h-[16px] md:h-[auto]", item.imgClassName)} />
                {/* 각 페이지가 title 로 <p> 를 넘기므로 래퍼는 span (p 안에 p 중첩 방지) */}
                <span className="text-left font-['GongGothicMedium'] text-[16px] text-white md:text-[24px]">
                    {item.title}
                </span>
            </div>
        </AccordionTrigger>
    );
}

export default function ProgramAccordion({
    list,
    className,
    triggerClassName,
    contentClassName,
    defaultOpenItems = [],
    pattern,
    deco,
    disableToggle = false,
    hideItemBorder = false,
    design = "legacy",
}: ProgramAccordionProps) {
    // disableToggle이 true면 모든 아이템 열림
    const allOpenValues = list.map((_, i) => `program_${i}`);
    const defaultValues = disableToggle ? allOpenValues : defaultOpenItems.map(index => `program_${index}`);

    return (
        <Accordion
            type="multiple"
            // 카드 사이 간격: 2026 디자인은 PC·모바일 모두 20px, 아카이브(legacy)는 기존 8px 유지
            className={twMerge("flex flex-col", design === "2026" ? "gap-[20px]" : "gap-[8px]", className)}
            {...(disableToggle ? { value: allOpenValues, onValueChange: () => {} } : { defaultValue: defaultValues })}
        >
            {list.map((item, i) => {
                // 접기/펼치기가 없는 경우(내용 없음 또는 토글 비활성) 화살표 숨김 + 커서 기본
                const isStatic = !item.contents || disableToggle;
                const theme2026 = design === "2026" ? PROGRAM_DESIGN_2026[item.background] : undefined;

                return (
                    <AccordionItem
                        key={`program_${i}`}
                        value={`program_${i}`}
                        className={hideItemBorder ? "border-b-0" : undefined}
                    >
                        {theme2026 ? (
                            <Trigger2026
                                item={item}
                                triggerClassName={triggerClassName}
                                isStatic={isStatic}
                                theme={theme2026}
                            />
                        ) : (
                            <TriggerLegacy
                                item={item}
                                triggerClassName={triggerClassName}
                                isStatic={isStatic}
                                pattern={pattern}
                                deco={deco}
                            />
                        )}
                        {item.contents && (
                            <AccordionContent
                                className={twMerge(
                                    "p-[8px] text-[13px] bg-white font-['GongGothicLight'] leading-[1.5] md:text-[16px] md:p-[18px]",
                                    contentClassName,
                                )}
                            >
                                {item.contents}
                            </AccordionContent>
                        )}
                    </AccordionItem>
                );
            })}
        </Accordion>
    );
}
